import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbCalendar, NgbDateStruct, NgbDatepickerConfig, NgbDatepickerModule, NgbTooltipModule, NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { UsuarioService } from '@account/usuario.service';
import { MessageService } from '@shell/message.service';
import { Usuario } from '@shared/models/despacho/Usuario';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { ControlCreditoService } from '@controlCredito/control-credito.service';
import { NivelControlCredito, UsuarioNivelControlCredito } from '@shared/models/controlCredito/NivelControlCredito';
import { ErrorModel } from '@shared/models/ErrorModel';
import { SharedService } from '@shared/shared.service';

@Component({
  selector: 'app-modal-usuarios-delegaciones',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NgbTypeaheadModule,
    NgbDatepickerModule,
    NgbTooltipModule
  ],
  templateUrl: './modal-usuarios-delegaciones.component.html',
  styleUrls: ['./modal-usuarios-delegaciones.component.scss']
})
export class ModalUsuariosDelegacionesComponent implements OnInit {
  @Input() esDelegacion = false;
  @Input() nivelSeleccionado: NivelControlCredito;
  form!: FormGroup;
  obteniendoUsuarios = false;
  usuarios: Usuario[] = [];
  errorUsuarios = false;
  usuarioSeleccionado!: Usuario;
  guardandoUsuario: boolean;

  modelDesde!: NgbDateStruct;
  modelHasta!: NgbDateStruct;

  constructor(
    private fb: FormBuilder,
    private usuarioService: UsuarioService,
    private messageService: MessageService,
    private calendar: NgbCalendar,
    private datepickerConfig: NgbDatepickerConfig,
    private activeModal: NgbActiveModal,
    private controlCreditoService: ControlCreditoService,
    private sharedService: SharedService
  ) {
    const today = this.calendar.getToday();
    this.datepickerConfig.minDate = today;
    this.datepickerConfig.maxDate = this.addDaysToDate(today, 30);
  }

  ngOnInit(): void {
    this.crearForm();
    this.getUsuarios();
  }
  private crearForm(): void {
    this.form = this.fb.group({
      usuarioFormControl: [null, Validators.required]
    });

    if (this.esDelegacion) {
      this.form.addControl(
        'fechaDesdeFormControl',
        this.fb.control(null, Validators.required)
      );
      this.form.addControl(
        'fechaHastaFormControl',
        this.fb.control(null, Validators.required)
      );
      this.form.addControl(
        'motivoFormControl',
        this.fb.control(null, Validators.required)
      );

      const today = this.calendar.getToday();
      this.modelDesde = today;
      this.modelHasta = today;
      this.form.get('fechaDesdeFormControl')?.setValue(today);
      this.form.get('fechaHastaFormControl')?.setValue(today);

      this.form.setValidators(this.validarFechas.bind(this));
      this.form.updateValueAndValidity();
    }
  }

  private addDaysToDate(date: NgbDateStruct, days: number): NgbDateStruct {
    const jsDate = new Date(date.year, date.month - 1, date.day);
    jsDate.setDate(jsDate.getDate() + days);
    return {
      year: jsDate.getFullYear(),
      month: jsDate.getMonth() + 1,
      day: jsDate.getDate()
    };
  }

  getUsuarios(): void {
    this.obteniendoUsuarios = true;
    this.usuarioService.obtenerTodosLosUsuarios(true, false)
    .subscribe(
      result => {
        this.usuarios = result.filter(u =>
          !this.nivelSeleccionado.usuarios?.some(asignado => asignado.usuario.dni === u.dni) &&
          !this.nivelSeleccionado.delegaciones?.some(asignado => asignado.usuario.dni === u.dni)
        );
        this.obteniendoUsuarios = false;
      },
      () => {
        this.obteniendoUsuarios = false;
        this.errorUsuarios = true;
        this.messageService.showErrorMessage('Error al obtener usuarios.');
      }
    );
  }

  searchUsuario = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term =>
        this.usuarios == null || term.length < 2 || this.obteniendoUsuarios
          ? []
          : this.usuarios.filter(u => this.containsString(term, u.nombresCompletos)).slice(0, 10)
      )
    );

  formatterUsuario = (u: Usuario) => u.nombresCompletos;

  selectUsuario(event: any): void {
    this.form.get('usuarioFormControl')?.setValue(event.item);
    this.usuarioSeleccionado = event.item;
  }

  private containsString(searchValue: string, searchIn: string): boolean {
    return searchIn.toLowerCase().includes(searchValue.toLowerCase());
  }

  verificarUsuarioSeleccionado(): void {
    const control = this.form.get('usuarioFormControl');
    if (control?.value && typeof control.value === 'string') {
      control.reset();
      this.usuarioSeleccionado = null;
      control.markAsTouched();
    }
  }

  onCancelar() {
    this.activeModal.dismiss();
  }

  onGuardar() {
    this.guardandoUsuario = true;

    const dto = new UsuarioNivelControlCredito();
    dto.nivelId = this.nivelSeleccionado.id;
    dto.usuario = this.usuarioSeleccionado;
    if(this.esDelegacion) {
      const fechaHasta: NgbDateStruct = this.fechaHastaFormControl.value;
      const fechaDesde: NgbDateStruct = this.fechaDesdeFormControl.value;
      dto.fechaDesde = this.sharedService.formatDate(fechaDesde.year, fechaDesde.month, fechaDesde.day, 0 ,0)
      dto.fechaHasta = this.sharedService.formatDate(fechaHasta.year, fechaHasta.month, fechaHasta.day, 23, 59);
      dto.motivo = this.motivoFormControl.value;
      const usuarioOtorgador = new Usuario();
      usuarioOtorgador.id = this.usuarioService.getIdUsuario();
      usuarioOtorgador.nombresCompletos = this.usuarioService.getNombresCompletosUsuario();
      usuarioOtorgador.dni = this.usuarioService.getDniUsuario();
      usuarioOtorgador.email = this.usuarioService.getEmailUsuario();
      usuarioOtorgador.aeroplantas = this.usuarioService.getAeroplantasCurrentUser();
      dto.usuarioOtorgador = usuarioOtorgador;
      this.agregarDelegacion(dto);
    } else this.agregarUsuario(dto);
  }

  agregarUsuario(dto) {
    this.controlCreditoService.agregarUsuario(dto).subscribe(u => {
      this.guardandoUsuario = false;
      this.activeModal.close(u)
    }, (err: ErrorModel) => {
      this.guardandoUsuario = false;
      this.messageService.showErrorMessage(err.message + "\n" + err.description)
    })
  }
  
  agregarDelegacion(dto) {
    this.controlCreditoService.agregarDelegacion(dto).subscribe(u => {
      this.guardandoUsuario = false;
      this.activeModal.close(u)
    }, (err: ErrorModel) => {
      this.guardandoUsuario = false;
      this.messageService.showErrorMessage(err.message + "\n" + err.description)
    })
  }

  validarFechas(group: FormGroup) {
    const desdeModel = group.get('fechaDesdeFormControl')?.value;
    const hastaModel = group.get('fechaHastaFormControl')?.value;

    if (!desdeModel || !hastaModel) {
      return null;
    }

    const desde = new Date(desdeModel.year, desdeModel.month - 1, desdeModel.day);
    const hasta = new Date(hastaModel.year, hastaModel.month - 1, hastaModel.day);

    return hasta >= desde ? null : { fechaInvalida: true };
  }


  get fechaHastaFormControl() {
    return this.form.get('fechaHastaFormControl')
  }

  get fechaDesdeFormControl() {
    return this.form.get('fechaDesdeFormControl')
  }

  get motivoFormControl() {
    return this.form.get('motivoFormControl')
  }

}